import type { Task, TaskStatus } from './types';

export const COLUMNS: Array<{ status: TaskStatus; label: string; accent: string }> = [
  { status: 'todo', label: 'To do', accent: 'bg-todo' },
  { status: 'in_progress', label: 'In progress', accent: 'bg-progress' },
  { status: 'blocked', label: 'Blocked', accent: 'bg-blocked' },
  { status: 'done', label: 'Done', accent: 'bg-done' },
];

/** Smallest gap worth halving before the column is spread out again instead. */
const MIN_SPAN = 1e-6;

function completedKey(task: Task): number {
  return Date.parse(task.completedAt ?? task.updatedAt);
}

/**
 * The order a column is read in. The three working columns are hand-ordered, so
 * they follow `position`; Done is a record of what happened, so the most recent
 * completion sits at the top — which is also where a task lands the moment
 * somebody ticks it off.
 */
export function sortColumn(tasks: Task[], status: TaskStatus): Task[] {
  const sorted = tasks.slice();
  if (status === 'done') {
    sorted.sort((a, b) => completedKey(b) - completedKey(a));
    return sorted;
  }
  sorted.sort((a, b) => a.position - b.position || a.createdAt.localeCompare(b.createdAt));
  return sorted;
}

export type Placement = {
  /** The position to write on the task that moved. */
  position: number;
  /**
   * Non-empty only when the neighbours have run out of room between them: the
   * whole destination column is then spread back out, and these go with the move.
   */
  spread: Array<{ id: string; position: number }>;
};

/**
 * Where a card dropped at `index` of `column` belongs. `column` is the
 * destination in display order with the dragged card already taken out.
 *
 * Halving the gap between the two neighbours keeps a drop to a single write.
 * Doubles run out after ~50 drops into the same slot, and legacy rows can share a
 * position outright, so the fallback renumbers the column — rare, and it restores
 * room for another 50.
 */
export function placeInColumn(column: Task[], index: number): Placement {
  const before = column[index - 1];
  const after = column[index];

  if (!before && !after) return { position: 0, spread: [] };
  if (!before) return { position: after!.position - 1, spread: [] };
  if (!after) return { position: before.position + 1, spread: [] };

  if (after.position - before.position > MIN_SPAN) {
    return { position: (before.position + after.position) / 2, spread: [] };
  }

  return {
    position: index * 2 - 1,
    spread: column.map((task, i) => ({ id: task.id, position: i * 2 })),
  };
}
